import React from 'react';

// Composant dédié à l'impression du certificat
// Mise en page professionnelle optimisée pour l'impression A4
const PrintableCertificate = ({ certificate }) => {
  const competenciesLabels = {
    learning_ability: { label: 'Capacité d\'apprentissage', desc: 'il/elle a bien compris en quoi consistait sa mission pour en maîtriser la pratique' },
    autonomy: { label: 'Autonomie', desc: 'il/elle a exercé son activité sans avoir besoin d\'une supervision' },
    adaptability: { label: 'Adaptabilité', desc: 'il/elle a réagi avec pertinence pour modifier sa façon de faire face aux problèmes rencontrés' },
    creativity: { label: 'Créativité', desc: 'il/elle a proposé des idées pour rendre son activité plus efficace ou plus conviviale' },
    sociability: { label: 'Sociabilité', desc: 'il/elle a facilement trouvé sa place parmi les autres membres du groupe' },
    cooperation: { label: 'Coopération', desc: 'dans son action, il/elle a tenu compte de l\'activité des autres membres de son équipe' },
    empathy: { label: 'Empathie', desc: 'dans les moments de tension, il/elle a su se mettre à la place de l\'autre pour comprendre son point de vue et éviter les conflits' },
    leadership: { label: 'Leadership', desc: 'il/elle a montré des capacités pour motiver l\'activité des autres et les solliciter en leur apportant si nécessaire un conseil ou un appui' },
    rigor_reliability: { label: 'Rigueur et fiabilité', desc: 'il/elle a exercé son activité dans le respect des règles, de pratiques et des valeurs de l\'association' },
    responsibility: { label: 'Responsabilité', desc: 'il/elle s\'est senti(e) personnellement concerné(e) par la bonne réalisation des tâches ou la conduite des projets jusqu\'à leur accomplissement' },
    curiosity: { label: 'Curiosité', desc: 'il/elle s\'est intéressé(e) à la vie de l\'association, à son projet associatif et à ses diverses activités' },
    engagement: { label: 'Engagement', desc: 'il/elle s\'est montré(e) prêt(e) à prendre des responsabilités dans l\'animation, la vie collective ou le développement de l\'association' }
  };

  const categorizedCompetencies = {
    action: ['learning_ability', 'autonomy', 'adaptability', 'creativity'],
    team: ['sociability', 'cooperation', 'empathy', 'leadership'],
    association: ['rigor_reliability', 'responsibility', 'curiosity', 'engagement']
  };

  const selectedCompetencies = Object.entries(certificate.competencies || {})
    .filter(([_, value]) => value)
    .map(([key]) => key);

  const categoryLabels = {
    action: 'Dans l\'action',
    team: 'Dans l\'équipe',
    association: 'Dans l\'association'
  };

  return (
    <div className="printable-certificate" data-testid="printable-certificate">
      {/* En-tête avec logo */}
      <div className="print-header">
        <div className="print-logo-section">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/1472cd9c-8a7f-43cf-aa2a-b29db8623d83/images/7ac324b5faacd922709a8d3826620defd5dabcad191de1fdd64d06e256d8e86a.png"
            alt="Bénévol'Check"
            style={{ width: '70px', height: '70px' }}
          />
          <div className="print-title">
            <h1>Bénévol'Check</h1>
            <p className="print-subtitle">Attestation de compétences bénévoles</p>
          </div>
        </div>
      </div>

      {/* Ligne de séparation décorative */}
      <div className="print-divider"></div>

      {/* Corps du certificat */}
      <div className="print-body">
        {/* Info association */}
        <div className="print-association-info">
          <p><strong>L'association</strong> <span className="print-highlight">{certificate.association_name}</span></p>
          <p><strong>Représentée par</strong> <span className="print-value">{certificate.signatory_name}</span></p>
        </div>

        {/* Bloc principal - Témoigne que */}
        <div className="print-main-statement">
          <p className="print-certifies">Témoigne que</p>
          <p className="print-volunteer-name">{certificate.volunteer_name}</p>
          <p className="print-was-volunteer">A été bénévole dans notre association</p>
        </div>

        {/* Période et volume horaire */}
        <div className="print-details-grid">
          <div className="print-detail-box">
            <span className="print-detail-label">Période d'engagement</span>
            <span className="print-detail-value">
              Du {new Date(certificate.start_date).toLocaleDateString('fr-FR')} au {new Date(certificate.end_date).toLocaleDateString('fr-FR')}
            </span>
          </div>
          <div className="print-detail-box">
            <span className="print-detail-label">Volume horaire</span>
            <span className="print-detail-value print-hours">{certificate.hours_volume} heures</span>
          </div>
        </div>

        {/* Mission */}
        <div className="print-mission">
          <strong>Mission réalisée :</strong> {certificate.mission_description}
        </div>

        {/* Compétences */}
        {selectedCompetencies.length > 0 && (
          <div className="print-competencies">
            <h2>Au cours de cette mission, il/elle a fait preuve de :</h2>
            
            {Object.entries(categorizedCompetencies).map(([category, skills]) => {
              const relevantSkills = skills.filter(key => certificate.competencies[key]);
              if (relevantSkills.length === 0) return null;
              
              return (
                <div key={category} className="print-competency-category">
                  <h3>{categoryLabels[category]}</h3>
                  <ul>
                    {relevantSkills.map(key => (
                      <li key={key}>
                        <strong>{competenciesLabels[key].label}</strong> : {competenciesLabels[key].desc}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {/* Commentaire du responsable */}
        {certificate.comment && (
          <div className="print-comment">
            <h3>Observations du responsable associatif</h3>
            <p>{certificate.comment}</p>
          </div>
        )}

        {/* Signature */}
        {certificate.status === 'validated' && (
          <div className="print-signature">
            <div className="print-signature-left">
              <p>Fait à <strong>{certificate.signing_city || certificate.association_name}</strong></p>
              <p>Le <strong>{new Date(certificate.validation_date).toLocaleDateString('fr-FR')}</strong></p>
            </div>
            <div className="print-signature-right">
              <p className="print-signature-label">Signature du responsable</p>
              <div className="print-signature-box"></div>
              <p className="print-signatory-name">{certificate.signatory_name}</p>
            </div>
          </div>
        )}
      </div>

      {/* Pied de page */}
      <div className="print-footer">
        <p>Document généré par Bénévol'Check — Plateforme d'attestation de compétences bénévoles</p>
      </div>
    </div>
  );
};

export default PrintableCertificate;
